/**
 * HTTP Implementation of Trophy Room Gateway
 * Story 3.6.d: Owner Trophy Room Management Frontend
 *
 * Handles authenticated owner Trophy Room operations via REST API.
 * Requires Cognito JWT authentication token.
 *
 * Backend Endpoints (Story 3.6.b):
 * - GET /api/v1/owner/trophy-room/items
 * - PUT /api/v1/owner/trophy-room/items/:itemId/visibility
 * - POST /api/v1/owner/trophy-room/items/:itemId/unpair (with optional reportType)
 */

import type {
  ITrophyRoomGateway,
  TrophyRoomItem,
  GetUserItemsResponse,
  ReportType,
} from '@domain/ports/ITrophyRoomGateway';

export class HttpTrophyRoomGateway implements ITrophyRoomGateway {
  private baseUrl: string;
  private getAuthToken: () => string | null;

  constructor(baseUrl: string, getAuthToken: () => string | null) {
    this.baseUrl = baseUrl;
    this.getAuthToken = getAuthToken;
  }

  /**
   * Generic HTTP request helper with JWT authorization
   */
  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getAuthToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Add Authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.log('[HttpTrophyRoomGateway] Making request:', {
      path,
      method: options.method || 'GET',
      hasToken: !!token,
    });

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
    });

    console.log('[HttpTrophyRoomGateway] Response status:', {
      path,
      status: response.status,
      ok: response.ok,
      statusText: response.statusText,
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: 'Unknown error' }));
      console.error('[HttpTrophyRoomGateway] Request failed:', error);

      // Handle specific HTTP status codes
      if (response.status === 401) {
        throw new Error('Unauthorized: Please log in to continue.');
      }

      if (response.status === 403) {
        throw new Error('You do not have permission to perform this action.');
      }

      if (response.status === 404) {
        throw new Error('Item not found. It may have been deleted.');
      }

      // Extract error message from backend (Zod validation or domain errors)
      const errorMessage =
        error.errors?.[0]?.message ||
        error.error ||
        error.message ||
        `HTTP error ${response.status}`;
      throw new Error(errorMessage);
    }

    const jsonData = await response.json();
    return jsonData;
  }

  async getUserItems(): Promise<GetUserItemsResponse> {
    console.log('[HttpTrophyRoomGateway] Fetching Trophy Room items');

    try {
      const response = await this.request<GetUserItemsResponse>(
        '/api/v1/owner/trophy-room/items'
      );

      console.log('[HttpTrophyRoomGateway] Items fetched successfully:', {
        totalCount: response.totalCount,
        itemsLength: response.items.length,
      });

      return response;
    } catch (error) {
      console.error('[HttpTrophyRoomGateway] Failed to fetch items:', error);

      // Re-throw with more context if it's a network error
      if (error instanceof Error) {
        if (
          error.message === 'Failed to fetch' ||
          error.message.includes('network')
        ) {
          throw new Error('NetworkError: Failed to fetch items');
        }
        throw error;
      }

      throw new Error('Unknown error occurred while fetching items');
    }
  }

  async updateItemVisibility(
    itemId: string,
    visibility: 'public' | 'private'
  ): Promise<TrophyRoomItem> {
    console.log('[HttpTrophyRoomGateway] Updating item visibility:', {
      itemId,
      visibility,
    });

    try {
      // Backend expects boolean visibility in request body
      const response = await this.request<{ item: TrophyRoomItem }>(
        `/api/v1/owner/trophy-room/items/${encodeURIComponent(itemId)}/visibility`,
        {
          method: 'PUT',
          body: JSON.stringify({ visibility: visibility === 'public' }),
        }
      );

      console.log('[HttpTrophyRoomGateway] Visibility updated successfully:', {
        itemUrl: response.item.url,
        newVisibility: response.item.visibility,
      });

      return response.item;
    } catch (error) {
      console.error('[HttpTrophyRoomGateway] Failed to update visibility:', error);

      if (error instanceof Error) {
        if (
          error.message === 'Failed to fetch' ||
          error.message.includes('network')
        ) {
          throw new Error('NetworkError: Failed to update visibility');
        }
        throw error;
      }

      throw new Error('Unknown error occurred while updating visibility');
    }
  }

  async unpairItem(itemId: string, reportType?: ReportType): Promise<void> {
    console.log('[HttpTrophyRoomGateway] Unpairing item:', {
      itemId,
      reportType
    });

    try {
      // Build request with optional reportType in body
      const requestOptions: RequestInit = {
        method: 'POST',
      };

      // Include reportType in body if provided
      if (reportType) {
        requestOptions.body = JSON.stringify({ reportType });
      }

      await this.request<{ success: boolean; message: string }>(
        `/api/v1/owner/trophy-room/items/${encodeURIComponent(itemId)}/unpair`,
        requestOptions
      );

      console.log('[HttpTrophyRoomGateway] Item unpaired successfully:', {
        itemId,
        reportType,
      });
    } catch (error) {
      console.error('[HttpTrophyRoomGateway] Failed to unpair item:', error);

      if (error instanceof Error) {
        if (
          error.message === 'Failed to fetch' ||
          error.message.includes('network')
        ) {
          throw new Error('NetworkError: Failed to unpair item');
        }
        throw error;
      }

      throw new Error('Unknown error occurred while unpairing item');
    }
  }

}
